import { useEffect } from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from './Button';
import FormInput from './FormInput';
import Modal from './Modal';
import { useTranslation } from 'react-i18next';
import i18n from '../common/config/i18n';
import { ClientStatus, IClient, IClientDTO } from '../common/interfaces/client.interface';
import Toggle from './Toggle';

interface ClientModalProps {
  title: string;
  onClose: () => void;
  onSubmit: (client: IClientDTO) => void;
  buttonLabel: string;
  client?: IClient | null;
}

const schema = yup
  .object({
    name: yup.string().required(i18n.t('client_modal:name.errors.required')),
    description: yup.string().notRequired(),
    cui: yup.string().notRequired(),
    raf_number: yup.string().notRequired(),
    address: yup.string().notRequired(),
  })
  .required();

const ClientModal = ({ title, onClose, onSubmit, buttonLabel, client }: ClientModalProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<any>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  const { t } = useTranslation('client_modal');

  useEffect(() => {
    if (client) {
      reset({ ...client, status: client.status === ClientStatus.ACTIVE ? true : false });
    }
  }, [client]);

  return (
    <Modal title={title} onClose={onClose}>
      <div className="flex flex-col px-12 w-full gap-[60px]">
        <form className="gap-4" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            key="name"
            name="name"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <FormInput
                  type="text"
                  errorMessage={errors.name?.message?.toString()}
                  value={value}
                  label={t('name.label')}
                  onChange={onChange}
                  helper={t('name.helper')}
                />
              );
            }}
          />
          <Controller
            key="description"
            name="description"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <FormInput
                  type="text"
                  errorMessage={errors.description?.message?.toString()}
                  value={value}
                  label={t('description.label')}
                  onChange={onChange}
                  helper={t('description.helper')}
                />
              );
            }}
          />
          <Controller
            key="cui"
            name="cui"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <FormInput
                  type="text"
                  errorMessage={errors.cui?.message?.toString()}
                  value={value}
                  label={t('cui.label')}
                  onChange={onChange}
                  helper={t('cui.helper')}
                />
              );
            }}
          />
          <Controller
            key="raf_number"
            name="raf_number"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <FormInput
                  type="text"
                  errorMessage={errors.rafNumber?.message?.toString()}
                  value={value}
                  label={t('rafNumber.label')}
                  onChange={onChange}
                  helper={t('rafNumber.helper')}
                />
              );
            }}
          />
          <Controller
            key="address"
            name="address"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <FormInput
                  type="text"
                  errorMessage={errors.address?.message?.toString()}
                  value={value}
                  label={t('address.label')}
                  onChange={onChange}
                  helper={t('address.helper')}
                />
              );
            }}
          />
          {client && (
            <Controller
              key="status"
              name="status"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <Toggle
                    id="client_status__toggle"
                    label={t('status.label')}
                    enabledText={t('status.active')}
                    disabledText={t('status.inactive')}
                    onChange={onChange}
                    value={value}
                  />
                );
              }}
            />
          )}
        </form>
        <div className="flex flex-row-reverse">
          <Button
            id="client_form__submit-button"
            label={buttonLabel}
            className="btn-primary w-full"
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ClientModal;
